import { createContext, useContext } from "react";

/**
 * What a host page has to say about individual rows of `DocumentListTable`,
 * without threading props through the views that mount it. The Brief page
 * uses it to mark what confirming would record (a new or changed file, a
 * folder with changes inside) and to outline the files a hovered suggestion
 * cites.
 *
 * Ids are the row ids the table renders: a document's id, a folder's id.
 */
export interface RowChip {
  label: string;
  /** added → green, changed → amber, removed → red. */
  tone: "added" | "changed" | "removed";
  /** Spelled out for screen readers and in the tooltip. */
  title?: string;
}

export interface DocumentRowDecorations {
  documentChip?: (documentId: string) => RowChip | null;
  folderChip?: (folderId: string) => RowChip | null;
  /** Rows to outline in red (comment-surface convention). */
  isHighlighted?: (kind: "document" | "folder", id: string) => boolean;
  /**
   * Every document row keeps one height whatever its columns: tags and the
   * size/date line each stay on one line rather than wrapping. The Brief page
   * shows and hides the Tags column as Franklin's suggestions close and open,
   * and its rows must not move when it does.
   */
  singleLineRows?: boolean;
}

export const DocumentRowDecorationsContext = createContext<DocumentRowDecorations | null>(null);

export function useDocumentRowDecorations(): DocumentRowDecorations | null {
  return useContext(DocumentRowDecorationsContext);
}

/** Classes for an outlined row: a red ring inside the row's own box. */
export const HIGHLIGHTED_ROW_CLASS = "relative z-[1] ring-1 ring-inset ring-red-600";
